// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	ReactNode,
	SetStateAction,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

// types
import {
	Category,
	Component,
	Contract,
	ContractAmount,
	ContractPayload,
	Entity,
	ErrorCause,
	ExpenseType,
	Feedback,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, SelectChangeEvent } from '@mui/material';

// fields
import { AmountsMultiCurrency } from '../../fields/AmountsMultiCurrency/AmountsMultiCurrency';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// utils
import { formatDate } from '../../../utils/dates';
import { getIds } from '../../../utils/objects';
import { translateError } from '../../../utils/translations';

// props
export interface FormManageContractProps {
	contract?: Contract;
	entity?: Entity;
	requestSource?: 'v2';
	/** Extra form fields rendered inside the same <form> after the description field. */
	extraFields?: ReactNode;
	/**
	 * Replace the legacy V1 blob-storage `FilePicker` with a custom uploader
	 * (rendered in the same position). Use this from V2 callers that upload
	 * via `uploadAiDocument` to the AI/bronze container instead of POST
	 * /documents (V1 blob storage). When provided, the V1 picker is hidden.
	 */
	filePickerSlot?: ReactNode;
	/**
	 * Async hook invoked after the V1 contract row is created successfully but
	 * before the success banner is shown and `onSuccess` / `onClose` fire. Use
	 * to perform follow-on writes (e.g. PATCH /v2/contracts/:id to apply V2
	 * spend-plan fields in the unified V2 create flow — see story #112).
	 * Throw to surface an error to the user.
	 */
	onAfterCreate?: (contractId: number) => Promise<void> | void;
	onClose?: () => void;
	onSuccess?: (contractId?: number) => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormManageContract = ({
	contract,
	entity,
	requestSource,
	extraFields,
	filePickerSlot,
	onAfterCreate,
	onClose,
	onSuccess,
	setIsLoading,
}: FormManageContractProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// defaults
	let defaultEntities: number[] = [];
	let defaultAmounts: ContractPayload['amounts'] = [];
	let defaultCategories: number[] = [];
	let defaultComponents: number[] = [];
	let defaultExpenseTypes: number[] = [];

	if (contract?.categories) {
		defaultCategories = getIds(contract.categories as Category[] | number[]);
	}

	if (contract?.components) {
		defaultComponents = getIds(contract.components as Component[] | number[]);
	}

	if (contract?.entities) {
		defaultEntities = (contract.entities as Entity[]).map((entity) => entity.id);
	}

	if (contract?.expenseTypes) {
		defaultExpenseTypes = getIds(contract.expenseTypes as ExpenseType[] | number[]);
	}

	if (contract?.amounts) {
		defaultAmounts = contract.amounts;
	} else {
		// Default to one empty amount for new contracts
		defaultAmounts = [
			{
				amount: 0,
				currency: '',
			},
		];
	}

	if (entity) {
		defaultEntities = [entity.id];
	}

	// state
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);

	// refs
	const feedbackRef = useRef<HTMLDivElement>(null);

	// Scroll the error/success banner into view whenever it appears so users
	// don't miss validation failures at the bottom of a tall form.
	useEffect(() => {
		if (feedback && feedbackRef.current) {
			requestAnimationFrame(() => {
				feedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			});
		}
	}, [feedback]);
	const [files, setFiles] = useState<File[]>([]);
	const [values, setValues] = useState({
		amounts: defaultAmounts,
		categories: defaultCategories,
		comment: '',
		components: defaultComponents,
		dateSigning: formatDate({ date: contract?.dateSigning, format: 'YYYY-MM-DD' }),
		description: contract?.description || '',
		entities: defaultEntities,
		expenseTypes: defaultExpenseTypes,
		idStep: contract?.idStep || '',
		num: contract?.num || '',
	});

	// vars
	const idForm = 'form-manage-contract';
	const isEdit = Boolean(contract);

	// fetch entities
	const urlEntities = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=SUPPLIER`;

	useFetch({
		url: urlEntities,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch categories
	const urlCategories = `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`;

	useFetch({
		isEnabled: Boolean(project?.id),
		url: urlCategories,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCategories(res.data);
			}
		},
	});

	// fetch components
	const urlComponents = `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`;

	useFetch({
		isEnabled: Boolean(project?.id),
		url: urlComponents,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComponents(res.data);
			}
		},
	});

	// fetch expense types
	const urlExpenseTypes = `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`;

	useFetch({
		isEnabled: Boolean(project?.id),
		url: urlExpenseTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseTypes(res.data);
			}
		},
	});

	// options
	const optionsCategories = categories.map((category) => ({
		label: category.name,
		value: category.id,
	}));

	const optionsComponents = components.map((component) => ({
		label: component.name,
		value: component.id,
	}));

	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: entity.id,
		};
	});

	const optionsExpenseTypes = expenseTypes.map((expenseType) => ({
		label: expenseType.name,
		value: expenseType.id,
	}));

	// handlers
	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleAmountsChange = (amounts: ContractAmount[] | ContractPayload['amounts']) => {
		setValues({
			...values,
			amounts: amounts as ContractAmount[],
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const entitiesNew = [...values.entities];

		if (!contract && userEntity?.id) {
			entitiesNew.push(userEntity.id);
		}

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// clear error
			setFeedback(null);

			// check for duplicate currencies
			const currenciesSelected = values.amounts?.map((amount) => amount.currency) || [];
			const currenciesUnique = new Set(currenciesSelected);
			if (currenciesSelected.length !== currenciesUnique.size) {
				feedbackNew.items.push({
					alert: {
						children: t('error.duplicateCurrency'),
						severity: 'error',
					},
				});
				setFeedback(feedbackNew);
				return;
			}

			// set loader
			setIsLoading(true);

			let commentId = null;
			let changedAmounts: ContractPayload['amounts'] = [];

			// If editing contract, check for amount changes
			if (isEdit) {
				// Compare current amounts with original amounts to find changes
				const originalAmounts = contract?.amounts || [];
				const currentCurrencies = values.amounts.map((amount) => amount.currency);
				const originalCurrencies = originalAmounts.map((amount) => amount.currency);

				// Find amounts that have changed (different values) or are new
				const modifiedAmounts = values.amounts.filter((currentAmount) => {
					const originalAmount = originalAmounts.find(
						(orig) => orig.currency === currentAmount.currency
					);

					// Include if it's a new currency or the amount has changed
					return !originalAmount || originalAmount.amount !== currentAmount.amount;
				});

				// Find currencies that were removed (set them to zero)
				const removedCurrencies = originalCurrencies.filter(
					(currency) => !currentCurrencies.includes(currency)
				);

				// Only include amounts that have changed or been removed
				const hasChanges = modifiedAmounts.length > 0 || removedCurrencies.length > 0;

				if (hasChanges) {
					changedAmounts = [
						// Include only modified/new amounts
						...modifiedAmounts.map((amount) => ({
							amount: amount.amount,
							currency: amount.currency,
						})),
						// Include removed currencies with zero amounts
						...removedCurrencies.map((currency) => ({
							amount: 0,
							currency: currency,
						})),
					];
				}

				if (values.comment) {
					const commentBody = {
						comment: values.comment,
						project: project?.id,
					};

					const fetchComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
						method: 'POST',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
							'Content-Type': 'application/json',
						},
						body: JSON.stringify(commentBody),
					});
					const resComment = await fetchComment.json();

					if (resComment.error) {
						throw new Error(resComment.error.message, {
							cause: {
								id: resComment.error.id,
								res: resComment,
							},
						});
					}

					commentId = resComment.data.id;
				}
			} else {
				// For new contracts, include all amounts
				changedAmounts = values.amounts.map((amount) => ({
					amount: amount.amount,
					currency: amount.currency,
				}));
			}

			const body: ContractPayload = {
				categories: values.categories || [],
				components: values.components || [],
				dateSigning: formatDate({ date: values.dateSigning, format: 'YYYY-MM-DD' }) || undefined,
				description: values.description,
				entities: entitiesNew,
				expenseTypes: values.expenseTypes || [],
				idStep: values.idStep,
				num: values.num,
			};

			// Only include project ID if creating new contract
			if (!isEdit) {
				body.project = project?.id;

				if (requestSource === 'v2') {
					body.status = 'DRAFT';
				}
			}

			// Only include amounts if there are changes (for edits) or if creating new contract
			if (changedAmounts.length > 0) {
				body.amounts = changedAmounts;
			}

			// Add comment reference for amendments
			if (commentId) {
				body.comment = commentId;
			}

			let url = `${process.env.REACT_APP_API_URL}/contracts`;
			if (contract) {
				url += `/${contract.id}`;
			}

			const fetchAddContract = await fetch(url, {
				method: isEdit ? 'PATCH' : 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resAddContract = await fetchAddContract.json();

			if (resAddContract.error) {
				throw new Error(resAddContract.error.message, {
					cause: {
						details: { ...resAddContract.error?.details, id: contract?.id, num: entries.num },
						id: resAddContract.error.id,
						res: resAddContract,
					},
				});
			}

			const contractNew = resAddContract.data;

			// add documents using files
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/contracts/${contractNew.id}/${year}/${month}/`
				);
				formData.append('file', files[i]);
				formData.append('contract', String(contractNew.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				const fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
					method: 'post',
					body: formData,
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({ alert: { severity: 'error' }, response: resPostDoc });
				}
			}

			if (onSuccess) {
				onSuccess(contractNew?.id);
			}

			// Allow callers to run follow-on writes (e.g. PATCH /v2/contracts/:id
			// for V2 spend-plan fields) before we fire the success banner. Runs on
			// both create and edit so the unified V2 form can persist activity,
			// commitment and custom-field changes. If this throws, it surfaces via
			// the catch block below and the banner is not shown.
			if (onAfterCreate && contractNew?.id) {
				await onAfterCreate(contractNew.id);
			}

			// banner message
			let bannerMessage = t('alert.contractCreated', { num: entries.num });
			if (isEdit) {
				bannerMessage = t('alert.contractUpdated', { num: entries.num });
			}

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				// set success banner
				setBanner({
					hasClose: true,
					message: bannerMessage,
					severity: 'success',
				});

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			// Default to translated generic error + ALWAYS use the `error` severity.
			const feedbackItem: Feedback = {
				alert: { children: translateError({ cause, t }), severity: 'error' },
			};

			if (cause) {
				if (cause.id === 'CONTRACT_DUPLICATE') {
					feedbackItem.alert.children = t('error.contractDuplicate', { num: entries.num });
				}

				if (cause.id === 'CONTRACT_ENTITIES_NON_EXISTENT') {
					feedbackItem.alert.children = t('error.contractSupplierEmpty');
				}

				if (cause.id === 'CONTRACT_ID_NON_EXISTENT') {
					feedbackItem.alert.children = t('error.contractIdNonExistent');
				}

				if (cause.id === 'CONTRACT_NON_EXISTENT') {
					feedbackItem.alert.children = t('error.contractNonExistent', { id: contract?.id });
				}

				if (cause.id === 'CONTRACT_UNAUTHORIZED_POST') {
					feedbackItem.alert.children = t('error.unauthorized');
				}

				if (cause.id === 'CONTRACT_COMMENT_REQUIRED') {
					feedbackItem.alert.children = t('error.contractCommentRequired');
				}

				if (cause.id === 'DOCUMENT_CONTRACT_NON_EXISTENT') {
					feedbackItem.alert.children = t('error.documentContractNonExistent');
				}

				// Fallback for any unmapped backend error id (e.g. new V2 codes like
				// CONTRACTS_EXCEED_ACTIVITY_BUDGET). Show the server's own message
				// so the user sees actionable info instead of "unknown error".
				if (
					feedbackItem.alert.children === t('error.default') &&
					typeof err.message === 'string' &&
					err.message.length > 0
				) {
					feedbackItem.alert.children = err.message;
				}

				// store full error response for debugging
				if (cause.res) {
					feedbackItem.response = cause.res;
				}
			} else if (typeof err.message === 'string' && err.message.length > 0) {
				// No structured cause — surface the raw error message (e.g. errors
				// thrown by `onAfterCreate` follow-on writes).
				feedbackItem.alert.children = err.message;
			}

			feedbackNew.items.push(feedbackItem);
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id={`${idForm}`} name="formManageContract" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-num`}
						label={t('referenceNumber')}
						name="num"
						onChange={handleChangeValue}
						required={true}
						value={values.num}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						disabled={entities.length === 0}
						id={`${idForm}-entities`}
						label={t('suppliers')}
						multiple={true}
						name="entities"
						onChange={handleChangeValue}
						options={optionsEntities}
						required={true}
						value={values.entities}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-categories`}
						label={t('categories')}
						multiple={true}
						name="categories"
						onChange={handleChangeValue}
						options={optionsCategories}
						value={values.categories}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-components`}
						label={t('components')}
						multiple={true}
						name="components"
						onChange={handleChangeValue}
						options={optionsComponents}
						value={values.components}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-expense-types`}
						label={t('expenseTypes')}
						multiple={true}
						name="expenseTypes"
						onChange={handleChangeValue}
						options={optionsExpenseTypes}
						value={values.expenseTypes}
					/>
				</Grid>
				<Grid item xs={12}>
					<AmountsMultiCurrency
						amounts={values.amounts}
						onChange={handleAmountsChange}
						idForm={idForm}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-step-id`}
						label={t('stepId')}
						name="idStep"
						onChange={handleChangeValue}
						required={false}
						value={values.idStep}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-date-signing`}
						inputProps={{
							max: new Date().toISOString().split('T')[0],
						}}
						label={t('signingDate')}
						name="dateSigning"
						onChange={handleChangeValue}
						required={false}
						type="date"
						value={values.dateSigning}
					/>
				</Grid>
				{!contract && (
					<Grid item xs={12}>
						{filePickerSlot !== undefined ? (
							filePickerSlot
						) : (
							<FilePicker
								onChange={(e, filesSelected) => {
									setFiles(filesSelected);
								}}
							/>
						)}
					</Grid>
				)}
				<Grid item xs={12}>
					<Input
						id={`${idForm}-description`}
						label={t('description')}
						minRows={2}
						multiline={true}
						name="description"
						onChange={handleChangeValue}
						value={values.description}
					/>
				</Grid>
				{isEdit && (
					<Grid item xs={12}>
						<Input
							id={`${idForm}-comment`}
							label={t('comment')}
							name="comment"
							onChange={handleChangeValue}
							required={true}
							multiline={true}
							minRows={2}
							value={values.comment}
						/>
					</Grid>
				)}

				{extraFields}

				{feedback && (
					<Grid item xs={12} ref={feedbackRef}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
